import { get, gt, gte, isInteger } from 'lodash';

import {
  DecimalNumber,
  decimalNumber,
} from '@app/shared/common/decimal-number/decimal-number';
import {
  findMeasureFactorValue,
  getSelectedMeasureId,
  updateInitialFactorAndCountValue,
} from '@app/shared/services/transfer/transfer.util';
import { REPLENISH_TYPE_VALUES } from '@app/shared/types/entities/transfer/transfer.enums';
import {
  InitialFactorAndCountValue,
  IQohInventoryItemVenue,
  ITransferFormFieldPath,
} from '@app/shared/types/entities/transfer/transfer.type';
import { MAX_NUMBER_FOR_TRANSFER_QUANTITY } from '@config/global-constants';
import { Threshold } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue/inventory-item-venue';
import { Primitive } from '@app/shared/types';
import { ReplenishQtyCalculator } from '../calculators/calculators.types';

/**
 * handle replenish value(fractional value) round up / down(and check min, max limit)
 *
 * @param value replenish value
 * @param options other value
 * @returns rounded value
 */
const getRoundedReplenishValue = (
  value: DecimalNumber,
  options: {
    threshold: Threshold;
    measureFactor: {
      item: DecimalNumber;
      threshold: DecimalNumber;
    };
    qohTo?: number;
    pendingQtyTo?: number;
    minLimit?: number;
    maxLimit?: number;
  },
): DecimalNumber => {
  const {
    maxLimit = MAX_NUMBER_FOR_TRANSFER_QUANTITY,
    minLimit = 0,
    qohTo = 0,
    pendingQtyTo = 0,
    threshold,
    measureFactor,
  } = options;

  const isIntegerOfReplenishValue = isInteger(value.valueOf());
  let roundedValue = value;

  if (!isIntegerOfReplenishValue) {
    // Round up, integer should not round up
    const roundedUpValue = value.ceil();

    const totalAmount = roundedUpValue
      .mul(measureFactor.item)
      .add(qohTo)
      .add(pendingQtyTo);

    const parValue = decimalNumber(get(threshold, REPLENISH_TYPE_VALUES.PAR));
    const parTotalAmount = parValue.mul(measureFactor.threshold);

    roundedValue =
      gt(totalAmount, parTotalAmount) && gt(roundedUpValue, 0)
        ? // rounded down
          value.floor()
        : roundedUpValue;
  }

  // Invalid value
  if (!roundedValue.isFinite) {
    return decimalNumber(0);
  }

  const maxNumber = decimalNumber(maxLimit).floor();

  // over the maximum boundary
  if (roundedValue.gt(maxNumber)) {
    return maxNumber;
  }

  const minNumber = decimalNumber(minLimit);

  // over the maximum boundary
  return roundedValue.gt(minNumber) ? roundedValue : decimalNumber(minNumber);
};

const calcValueByCalculator = (params: {
  item: IQohInventoryItemVenue;
  threshold: Threshold;
  replenishType: REPLENISH_TYPE_VALUES;
  percent: number;
  calculator: ReplenishQtyCalculator;
  additionalData?: Record<string, Primitive | object>;
}): DecimalNumber => {
  const {
    item,
    threshold,
    replenishType,
    percent,
    additionalData,
    calculator,
  } = params;

  // Gets measure factor value and config value for threshold
  const thresholdsMeasureFactorValue: DecimalNumber = decimalNumber(
    findMeasureFactorValue(item, threshold?.measure_entity_id),
  );

  const replenishMin = decimalNumber(get(threshold, REPLENISH_TYPE_VALUES.MIN));

  const [qohToNum, pendingQtyToNum] = (additionalData.qohTo ?? [
    0, 0,
  ]) as number[];

  // this already contains factor value
  const transferTo = decimalNumber(qohToNum).add(pendingQtyToNum);

  // “QOH(To) + Pending Qty(To)” is above or equal to Min, populate Qty Requested with 0.
  if (
    replenishMin &&
    !isNaN(replenishMin.valueOf()) &&
    gte(transferTo, 0) &&
    gte(transferTo, replenishMin.mul(thresholdsMeasureFactorValue).valueOf())
  ) {
    return decimalNumber(0);
  }

  const selectedMeasureId: string = getSelectedMeasureId(item);
  // Gets the factor value for selected measure.
  const selectedMeasureFactorValue: DecimalNumber = new DecimalNumber(
    findMeasureFactorValue(item, selectedMeasureId),
  );

  const measureFactor = {
    item: selectedMeasureFactorValue,
    threshold: thresholdsMeasureFactorValue,
  };

  const replenishmentValue = calculator({
    threshold,
    replenishType,
    percent,
    measureFactor,
    additionalData: {
      transferTo,
      ...additionalData,
    },
  });

  return getRoundedReplenishValue(replenishmentValue, {
    threshold,
    measureFactor,
    qohTo: qohToNum,
    pendingQtyTo: pendingQtyToNum,
  });
};

const replenishInventoryItem = (params: {
  item: IQohInventoryItemVenue;
  fieldPath: ITransferFormFieldPath;
  threshold: Threshold;
  replenishType?: REPLENISH_TYPE_VALUES;
  percent: number;
  calculator: ReplenishQtyCalculator;
  additionalData: Record<string, Primitive | object>;
}): IQohInventoryItemVenue & {
  initialFactorAndCountValue: InitialFactorAndCountValue;
} => {
  const {
    item,
    fieldPath,
    threshold,
    replenishType = REPLENISH_TYPE_VALUES.PAR,
    percent,
    calculator,
    additionalData,
  } = params;

  // u can add new param to custom this formula
  const replenishmentValue: DecimalNumber = calcValueByCalculator({
    item,
    threshold,
    replenishType,
    percent,
    calculator,
    additionalData: {
      ...additionalData,
      qohTo: item.qohTo,
    },
  });

  const initialCount = replenishmentValue.valueOf();
  // add measure entity id to find factor value
  const selectedMeasureId = getSelectedMeasureId(item);

  return {
    ...item,
    [fieldPath.initialCount]: initialCount,
    // add measure entity id to find factor value
    measure_entity_id: selectedMeasureId,
    initialFactorAndCountValue: updateInitialFactorAndCountValue({
      ...item,
      // add measure entity id to find factor value
      measure_entity_id: selectedMeasureId,
      [fieldPath.initialCount]: initialCount,
    }),
  };
};

export {
  replenishInventoryItem,
  calcValueByCalculator,
  getRoundedReplenishValue,
};
